import React, { useState, useEffect, useCallback } from 'react';
import { api, formatINR, formatDate, toInputDate } from '../utils/api';
import './ProjectDetail.css';
import './RetainerDetail.css';

export default function RetainerDetail({ project, allVendors, onRefresh }) {
  const [lineItems,   setLineItems]   = useState([]);
  const [cycles,      setCycles]      = useState([]);
  const [vendorPays,  setVendorPays]  = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [expandedLI,  setExpandedLI]  = useState(null);
  const [showAddLI,   setShowAddLI]   = useState(false);
  const [editLI,      setEditLI]      = useState(null);
  const [editVA,      setEditVA]      = useState(null);
  const [showEditProj,setShowEditProj]= useState(false);
  const [cycleToggle, setCycleToggle] = useState(null); // {cycle, field}

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/retainers/' + project.id + '/line-items'),
      api.get('/retainers/' + project.id + '/cycles'),
      api.get('/retainers/' + project.id + '/vendor-payments'),
    ]).then(([li, cy, vp]) => {
      setLineItems(li); setCycles(cy); setVendorPays(vp); setLoading(false);
    }).catch(() => setLoading(false));
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  async function handleStatusChange(e) {
    await api.patch('/projects/' + project.id + '/status', { status: e.target.value });
    onRefresh();
  }

  const monthlyValue = lineItems.reduce((s, li) => s + (li.client_cost || 0), 0);
  const monthlyGst    = lineItems.reduce((s, li) => s + (li.gst_amount || 0), 0);
  const monthlyTotal  = monthlyValue + monthlyGst;
  const vendorBudget  = lineItems.reduce((s, li) => s + (li.vendor_cost || 0), 0);
  const current       = cycles.find(c => c.is_current);
  const needsReconcile = cycles.filter(c => c.needs_reconciliation);

  return (
    <div className="page-content">

      <div className="card pd-header">
        <div className="pd-header-left">
          <div className="pd-proj-name">
            {project.name}
            <span className="badge badge-blue" style={{marginLeft:8, fontSize:10}}>Retainer</span>
          </div>
          <div className="pd-meta">
            <span className="pd-meta-item">👥 {project.client_name}</span>
            {project.start_date && (
              <span className="pd-meta-item">
                📅 {formatDate(project.start_date)}{project.end_date ? ' – ' + formatDate(project.end_date) : ' – open-ended'}
              </span>
            )}
            <span className="pd-meta-item">
              🗓 Invoicing: {project.invoicing_timing === 'start' ? 'beginning of month' : 'end of month'}
            </span>
            {project.link && (
              <a href={project.link} target="_blank" rel="noreferrer" className="pd-drive-link">🔗 Drive link</a>
            )}
          </div>
        </div>
        <div className="pd-header-right">
          <select className={'status-select ' + (project.status?.toLowerCase() || '')} value={project.status} onChange={handleStatusChange}>
            {['Incoming','Ongoing','Completed','Dropped'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button className="btn btn-outline" onClick={() => setShowEditProj(true)}>✎ Edit</button>
          <button className="btn btn-primary" onClick={() => setShowAddLI(true)}>+ Add Line Item</button>
        </div>
      </div>

      {needsReconcile.length > 0 && (
        <div className="reconcile-banner">
          ⚠ <strong>{needsReconcile.length}</strong> month{needsReconcile.length !== 1 ? 's' : ''} billed but never marked collected — see the flagged row{needsReconcile.length !== 1 ? 's' : ''} below.
        </div>
      )}

      <div className="pd-kpi-strip card">
        <div className="pd-kpi-block red-tint">
          <div className="pd-kpi-title">Monthly Value</div>
          <div className="pd-kpi-main red">{formatINR(monthlyTotal)}<span className="rt-permo">/mo</span></div>
          <div className="pd-kpi-breakdown">
            <div className="pd-kpi-row"><span>Template (ex-GST)</span><span>{formatINR(monthlyValue)}</span></div>
            <div className="pd-kpi-row"><span>+ GST</span><span>{formatINR(monthlyGst)}</span></div>
          </div>
        </div>
        <div className="pd-kpi-divider" />
        <div className="pd-kpi-block">
          <div className="pd-kpi-title">This Month{current ? ' — ' + current.period_month : ''}</div>
          <div className={'pd-kpi-main ' + (current?.collected ? 'green' : current?.billed ? '' : 'danger')}>
            {!current ? '—' : current.collected ? 'Collected' : current.billed ? 'Billed' : 'Not billed'}
          </div>
          <div className="pd-kpi-breakdown">
            <div className="pd-kpi-row"><span>Invoice date</span><span>{current ? formatDate(current.invoice_date) : '—'}</span></div>
          </div>
        </div>
        <div className="pd-kpi-divider" />
        <div className="pd-kpi-block">
          <div className="pd-kpi-title">Vendor Budget / mo</div>
          <div className="pd-kpi-main">{formatINR(vendorBudget)}</div>
          <div className="pd-kpi-breakdown">
            <div className="pd-kpi-row"><span>8020 margin / mo</span><span className="green">{formatINR(monthlyValue - vendorBudget)}</span></div>
          </div>
        </div>
      </div>

      <div className="card pd-li-card">
        <div className="card-header">
          <span className="card-title">Line Items (monthly template)</span>
          <span className="pd-hint">Applies every month automatically — click row to manage vendors</span>
        </div>
        <div className="table-wrap pd-li-scroll">
          <table>
            <thead>
              <tr>
                <th>Line Item</th>
                <th className="num">Cost / month</th>
                <th style={{textAlign:'center'}}>GST%</th>
                <th className="num">GST Amt</th>
                <th className="num">Client Total</th>
                <th className="num">Vendor Cost</th>
                <th className="num">8020 Margin</th>
                <th style={{textAlign:'center'}}>Vendors</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="pd-empty-row">Loading…</td></tr>
              ) : lineItems.length === 0 ? (
                <tr><td colSpan={9} className="pd-empty-row">No line items yet. Add the monthly scope above.</td></tr>
              ) : lineItems.map(li => (
                <React.Fragment key={li.id}>
                  <tr className={'pd-li-row ' + (expandedLI === li.id ? 'expanded' : '')} onClick={() => setExpandedLI(expandedLI === li.id ? null : li.id)}>
                    <td><span className="pd-li-name"><span className="pd-chevron">{expandedLI === li.id ? '▾' : '▸'}</span>{li.name}</span></td>
                    <td className="num">{formatINR(li.client_cost)}</td>
                    <td style={{textAlign:'center', color:'var(--text-light)'}}>{li.gst_rate}%</td>
                    <td className="num">{formatINR(li.gst_amount)}</td>
                    <td className="num">{formatINR(li.client_total)}</td>
                    <td className="num">{li.vendor_cost > 0 ? formatINR(li.vendor_cost) : <span className="text-light">—</span>}</td>
                    <td className="num"><span className="text-green fw-600">{formatINR(li.margin)}</span></td>
                    <td style={{textAlign:'center'}}><VendorBadge vendors={li.vendors} /></td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="pd-row-actions">
                        <button className="icon-btn edit" onClick={() => setEditLI(li)} title="Edit line item">✎</button>
                        <DeleteLIButton liId={li.id} hasVendors={li.vendors.length > 0} onDeleted={load} />
                      </div>
                    </td>
                  </tr>
                  {expandedLI === li.id && (
                    <tr className="pd-vendor-expand-row">
                      <td colSpan={9}>
                        <VendorExpandPanel li={li} allVendors={allVendors} onRefresh={load} onEditAssignment={(va) => setEditVA({ assignment: va, lineItem: li })} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
            {lineItems.length > 0 && (
              <tfoot>
                <tr>
                  <td>Total / month</td>
                  <td className="num">{formatINR(monthlyValue)}</td>
                  <td></td>
                  <td className="num">{formatINR(monthlyGst)}</td>
                  <td className="num">{formatINR(monthlyTotal)}</td>
                  <td className="num">{formatINR(vendorBudget)}</td>
                  <td className="num text-green fw-600">{formatINR(monthlyValue - vendorBudget)}</td>
                  <td></td><td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Billing Cycles</span>
          <span className="pd-hint">One row per month — past months are permanent, never reset</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Month</th>
                <th>Invoice Date</th>
                <th className="num">Amount</th>
                <th className="num">− TDS</th>
                <th className="num">Net</th>
                <th style={{textAlign:'center'}}>Billed</th>
                <th style={{textAlign:'center'}}>Collected</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="pd-empty-row">Loading…</td></tr>
              ) : cycles.length === 0 ? (
                <tr><td colSpan={8} className="pd-empty-row">No cycles yet — the current month appears once line items are added.</td></tr>
              ) : cycles.map(c => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.period_month}</strong>
                    {c.is_current && <span className="badge badge-blue" style={{marginLeft:6,fontSize:9}}>Current</span>}
                    {!!c.needs_reconciliation && <span className="reconcile-badge" style={{marginLeft:6}}>⚠ Reconcile</span>}
                  </td>
                  <td style={{fontSize:11,color:'var(--text-light)'}}>{formatDate(c.invoice_date)}</td>
                  <td className="num">{formatINR(c.amount + c.gst_amount)}</td>
                  <td className="num" style={{color:'var(--text-light)'}}>{c.tds_amount > 0 ? formatINR(c.tds_amount) : '—'}</td>
                  <td className="num fw-600">{c.collected ? formatINR(c.amount + c.gst_amount - c.tds_amount) : '—'}</td>
                  <td style={{textAlign:'center'}}>
                    <button className={'toggle-btn ' + (c.billed ? 'toggle-paid' : 'toggle-off')} onClick={() => setCycleToggle({ cycle: c, field: 'billed' })}>
                      <span className="t-dot" />{c.billed ? 'Billed' : 'Not billed'}
                    </button>
                  </td>
                  <td style={{textAlign:'center'}}>
                    <button className={'toggle-btn ' + (c.collected ? 'toggle-collected' : 'toggle-off')} onClick={() => setCycleToggle({ cycle: c, field: 'collected' })}>
                      <span className="t-dot" />{c.collected ? 'Collected' : 'Pending'}
                    </button>
                  </td>
                  <td style={{fontSize:10,color:'var(--text-light)'}}>
                    {c.billed_reference && <div>Ref: {c.billed_reference}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <VendorPaymentsPanel projectId={project.id} allVendors={allVendors} lineItems={lineItems} vendorPayments={vendorPays} onRefresh={load} />

      {showAddLI && (
        <LineItemModal projectId={project.id} onClose={() => setShowAddLI(false)} onSaved={() => { setShowAddLI(false); load(); }} />
      )}
      {editLI && (
        <LineItemModal projectId={project.id} lineItem={editLI} onClose={() => setEditLI(null)} onSaved={() => { setEditLI(null); load(); }} />
      )}
      {editVA && (
        <VendorAssignmentModal lineItem={editVA.lineItem} assignment={editVA.assignment} onClose={() => setEditVA(null)} onSaved={() => { setEditVA(null); load(); }} />
      )}
      {showEditProj && (
        <EditRetainerModal project={project} onClose={() => setShowEditProj(false)} onSaved={() => { setShowEditProj(false); onRefresh(); }} />
      )}
      {cycleToggle && (
        <CycleToggleModal cycle={cycleToggle.cycle} field={cycleToggle.field} onClose={() => setCycleToggle(null)} onSaved={() => { setCycleToggle(null); load(); }} />
      )}
    </div>
  );
}

/* ---- Vendor Badge ---- */
function VendorBadge({ vendors }) {
  if (!vendors || vendors.length === 0) return <span className="badge badge-gray">No vendor</span>;
  return <span className="badge badge-blue">{vendors.length} assigned</span>;
}

/* ---- Delete Line Item Button ---- */
function DeleteLIButton({ liId, hasVendors, onDeleted }) {
  async function handleDelete() {
    if (hasVendors) { alert('Remove vendor assignments first.'); return; }
    if (!window.confirm('Delete this line item? It will stop being billed from next month.')) return;
    try {
      await api.delete('/retainers/line-items/' + liId);
      onDeleted();
    } catch (err) { alert(err.message); }
  }
  return <button className="icon-btn" onClick={handleDelete} title="Delete line item">✕</button>;
}

/* ---- Vendor Expand Panel ---- */
function VendorExpandPanel({ li, allVendors, onRefresh, onEditAssignment }) {
  const [vendorId, setVendorId] = useState('');
  const [budget,   setBudget]   = useState('');
  const [notes,    setNotes]    = useState('');
  const [saving,   setSaving]   = useState(false);

  async function handleAssign() {
    if (!vendorId || !budget) return;
    setSaving(true);
    try {
      await api.post('/retainers/line-items/' + li.id + '/vendors', { vendor_id: parseInt(vendorId), budgeted_cost: parseFloat(budget), notes });
      setVendorId(''); setBudget(''); setNotes('');
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleRemoveVendor(vid) {
    if (!window.confirm('Remove this vendor assignment? It will stop applying from next month.')) return;
    try {
      await api.delete('/retainers/line-items/' + li.id + '/vendors/' + vid);
      onRefresh();
    } catch (err) { alert(err.message); }
  }

  const assignedIds = li.vendors.map(v => v.vendor_id);
  const available   = allVendors.filter(v => !assignedIds.includes(v.id));

  return (
    <div className="pd-vendor-panel">
      <div className="pd-vendor-panel-title">Vendors — {li.name} (applies every month)</div>
      {li.vendors.length > 0 && (
        <table className="pd-vendor-table">
          <tbody>
            {li.vendors.map(v => (
              <tr key={v.id}>
                <td><span className="pd-vdot" />{v.vendor_name}</td>
                <td>Budget <strong>{formatINR(v.budgeted_cost)}</strong>/mo</td>
                <td className="text-green">{v.paid_this_month > 0 ? 'Paid this month ' + formatINR(v.paid_this_month) : 'Not paid this month'}</td>
                <td>
                  <div className="pd-row-actions">
                    <button className="icon-btn edit" onClick={() => onEditAssignment(v)} title="Edit assignment">✎</button>
                    <button className="icon-btn" onClick={() => handleRemoveVendor(v.id)} title="Remove">✕</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {available.length > 0 && (
        <div className="pd-assign-row">
          <select className="form-select pd-assign-sel" value={vendorId} onChange={e => setVendorId(e.target.value)}>
            <option value="">Select vendor…</option>
            {available.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input className="form-input pd-assign-inp" type="number" placeholder="Budget ₹/mo" value={budget} onChange={e => setBudget(e.target.value)} />
          <input className="form-input pd-assign-inp" placeholder="Notes (opt)" value={notes} onChange={e => setNotes(e.target.value)} />
          <button className="btn btn-primary btn-sm" onClick={handleAssign} disabled={saving || !vendorId || !budget}>Assign</button>
        </div>
      )}
      {available.length === 0 && li.vendors.length === 0 && (
        <div className="pd-no-vendors">No vendors available. Add vendors in the Vendors module first.</div>
      )}
    </div>
  );
}

/* ---- Vendor Payments Panel ---- */
function VendorPaymentsPanel({ projectId, allVendors, lineItems, vendorPayments, onRefresh }) {
  const [form,   setForm]   = useState({ vendor_id:'', line_item_id:'', amount:'', gst_amount:'', payment_date:'', notes:'' });
  const [saving, setSaving] = useState(false);
  function setF(k, v) { setForm(f => ({...f, [k]: v})); }

  async function handleAdd() {
    if (!form.vendor_id || !form.amount) return;
    setSaving(true);
    try {
      await api.post('/retainers/vendor-payments', {
        project_id: parseInt(projectId), ...form,
        vendor_id: parseInt(form.vendor_id),
        line_item_id: form.line_item_id ? parseInt(form.line_item_id) : null,
        amount: parseFloat(form.amount), gst_amount: parseFloat(form.gst_amount) || 0
      });
      setForm({ vendor_id:'', line_item_id:'', amount:'', gst_amount:'', payment_date:'', notes:'' });
      onRefresh();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(pid) {
    if (!window.confirm('Delete this payment?')) return;
    await api.delete('/retainers/vendor-payments/' + pid);
    onRefresh();
  }

  return (
    <div className="card pd-payment-panel">
      <div className="card-header">
        <span className="card-title">Vendor Payments</span>
        <span className="pd-panel-sub">Recurring vendor costs, paid month to month</span>
      </div>
      <div className="pd-record-section">
        <div className="pd-record-title">Record Vendor Payment</div>
        <div className="pd-form-row">
          <select className="form-select" style={{flex:2}} value={form.vendor_id} onChange={e=>setF('vendor_id',e.target.value)}>
            <option value="">Select vendor…</option>
            {allVendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <select className="form-select" style={{flex:2}} value={form.line_item_id} onChange={e=>setF('line_item_id',e.target.value)}>
            <option value="">Line item (opt)…</option>
            {lineItems.map(li => <option key={li.id} value={li.id}>{li.name}</option>)}
          </select>
        </div>
        <div className="pd-form-row">
          <input className="form-input" placeholder="Amount" type="number" value={form.amount} onChange={e=>setF('amount',e.target.value)} />
          <input className="form-input" placeholder="GST (opt)" type="number" value={form.gst_amount} onChange={e=>setF('gst_amount',e.target.value)} />
          <input className="form-input" type="date" value={form.payment_date} onChange={e=>setF('payment_date',e.target.value)} />
        </div>
        <div className="pd-form-row">
          <input className="form-input" placeholder="Notes" value={form.notes} onChange={e=>setF('notes',e.target.value)} style={{flex:2}} />
        </div>
        <div style={{textAlign:'right'}}>
          <button className="btn btn-primary btn-sm" onClick={handleAdd} disabled={saving||!form.vendor_id||!form.amount}>
            {saving ? 'Saving…' : 'Add Payment'}
          </button>
        </div>
      </div>
      {vendorPayments.length > 0 && (
        <div className="pd-history">
          <div className="pd-record-title">Payment History</div>
          {vendorPayments.map(p => (
            <div key={p.id} className="pd-history-row">
              <div>
                <div className="pd-hist-amt">{p.vendor_name} — {formatINR(p.amount)}{p.gst_amount > 0 ? ' + ' + formatINR(p.gst_amount) + ' GST' : ''}</div>
                <div className="pd-hist-ref">{p.line_item_name || 'General'}</div>
              </div>
              <div style={{display:'flex', alignItems:'center', gap:8, flexShrink:0}}>
                <span className="pd-hist-date">{formatDate(p.payment_date)}</span>
                <button className="icon-btn" onClick={() => handleDelete(p.id)}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---- Line Item Modal ---- */
function LineItemModal({ projectId, lineItem, onClose, onSaved }) {
  const isEdit = !!lineItem;
  const [name,   setName]   = useState(lineItem?.name || '');
  const [cost,   setCost]   = useState(lineItem?.client_cost || '');
  const [gst,    setGst]    = useState(lineItem?.gst_rate ?? 18);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  async function handleSave() {
    if (!name || !cost) { setError('Name and cost are required.'); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put('/retainers/line-items/' + lineItem.id, { name, client_cost: parseFloat(cost), gst_rate: parseFloat(gst) });
      } else {
        await api.post('/retainers/line-items', { project_id: parseInt(projectId), name, client_cost: parseFloat(cost), gst_rate: parseFloat(gst) });
      }
      onSaved();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header"><div className="modal-title">{isEdit ? 'Edit Line Item' : 'Add Line Item'}</div></div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Line Item Name *</label>
            <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="e.g. SEO management" autoFocus />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cost per month (₹) *</label>
              <input className="form-input" type="number" value={cost} onChange={e=>setCost(e.target.value)} placeholder="40000" />
            </div>
            <div className="form-group">
              <label className="form-label">GST Rate (%)</label>
              <input className="form-input" type="number" value={gst} onChange={e=>setGst(e.target.value)} />
            </div>
          </div>
          <div className="type-hint">This bills automatically every month — no need to re-add it.</div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Add Line Item')}</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Vendor Assignment Modal ---- */
function VendorAssignmentModal({ lineItem, assignment, onClose, onSaved }) {
  const [budget, setBudget] = useState(assignment.budgeted_cost || '');
  const [notes,  setNotes]  = useState(assignment.notes || '');
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  async function handleSave() {
    if (!budget) { setError('Budget is required.'); return; }
    setSaving(true);
    try {
      await api.put('/retainers/line-items/' + lineItem.id + '/vendors/' + assignment.id, { budgeted_cost: parseFloat(budget), notes });
      onSaved();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">Edit Vendor Assignment</div>
          <div className="modal-sub">{assignment.vendor_name} · {lineItem.name}</div>
        </div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Budgeted Cost (₹/mo) *</label>
            <input className="form-input" type="number" value={budget} onChange={e=>setBudget(e.target.value)} autoFocus />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <input className="form-input" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Optional…" />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Edit Retainer Project Modal ---- */
function EditRetainerModal({ project, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: project.name,
    start_date: toInputDate(project.start_date),
    end_date: toInputDate(project.end_date),
    link: project.link || '',
    notes: project.notes || '',
    invoicing_timing: project.invoicing_timing || 'end',
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');
  function setF(k,v) { setForm(f => ({...f,[k]:v})); }

  async function handleSave() {
    setSaving(true); setError('');
    try {
      await api.put('/projects/' + project.id, form);
      onSaved();
    } catch(err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header"><div className="modal-title">Edit Retainer</div></div>
        <div className="modal-body">
          {error && <div className="setup-error">{error}</div>}
          <div className="form-group">
            <label className="form-label">Project Name</label>
            <input className="form-input" value={form.name} onChange={e=>setF('name',e.target.value)} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Start Date</label>
              <input className="form-input" type="date" value={form.start_date} onChange={e=>setF('start_date',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">End Date (optional)</label>
              <input className="form-input" type="date" value={form.end_date} onChange={e=>setF('end_date',e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Invoicing Date</label>
            <select className="form-select" value={form.invoicing_timing} onChange={e=>setF('invoicing_timing',e.target.value)}>
              <option value="start">Beginning of month (billed in advance)</option>
              <option value="end">End of month (billed after the month)</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Drive Link</label>
            <input className="form-input" value={form.link} onChange={e=>setF('link',e.target.value)} placeholder="https://drive.google.com/…" />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.notes} onChange={e=>setF('notes',e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
}

/* ---- Cycle Toggle Modal — Mark Billed / Mark Collected for one specific month ---- */
function CycleToggleModal({ cycle, field, onClose, onSaved }) {
  const [date, setDate]           = useState(new Date().toISOString().split('T')[0]);
  const [reference, setReference] = useState('');
  const [notes, setNotes]         = useState('');
  const [tds, setTds]             = useState('');
  const [saving, setSaving]       = useState(false);

  const isBilled = field === 'billed';
  const isCollected = field === 'collected';
  const isUndo   = isBilled ? !!cycle.billed : !!cycle.collected;
  const actionLabel = isBilled ? 'Billed to Client' : 'Collected from Client';
  const grossAmount = (cycle.amount || 0) + (cycle.gst_amount || 0);
  const netPreview = grossAmount - (parseFloat(tds) || 0);

  async function handleConfirm() {
    setSaving(true);
    try {
      const endpoint = '/retainers/cycles/' + cycle.id + '/' + field;
      const body = isUndo
        ? { [field]: false }
        : { [field]: true, date, reference, notes, ...(isCollected ? { tds_amount: parseFloat(tds) || 0 } : {}) };
      await api.patch(endpoint, body);
      onSaved();
    } catch (err) { alert(err.message); setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isUndo ? 'Undo — Mark as Not ' + (isBilled ? 'Billed' : 'Collected') + '?' : 'Mark as ' + actionLabel}</div>
          <div className="modal-sub">{cycle.period_month}</div>
        </div>
        {!isUndo && (
          <div className="modal-body">
            <div className="form-group"><label className="form-label">Date</label>
              <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} /></div>
            {isCollected && (
              <div className="form-group">
                <label className="form-label">TDS deducted by client (optional)</label>
                <input className="form-input" type="number" placeholder="0" value={tds} onChange={e => setTds(e.target.value)} />
                <div className="type-hint">Gross {formatINR(grossAmount)} − TDS = net {formatINR(netPreview)} received.</div>
              </div>
            )}
            <div className="form-group"><label className="form-label">Reference / Invoice No. (optional)</label>
              <input className="form-input" value={reference} onChange={e => setReference(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Notes (optional)</label>
              <input className="form-input" value={notes} onChange={e => setNotes(e.target.value)} /></div>
          </div>
        )}
        {isUndo && (
          <div className="modal-body">
            <div style={{fontSize:12, color:'var(--text-mid)'}}>
              This only affects {cycle.period_month} — other months are unaffected since each month keeps its own record.
            </div>
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn" style={{ background: isUndo ? 'var(--danger)' : 'var(--success)', color:'#fff' }} onClick={handleConfirm} disabled={saving}>
            {saving ? 'Saving…' : isUndo ? 'Yes, Undo' : 'Confirm ' + actionLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
